/**
 * Canonical client-side analytics helper.
 *
 * Pushes events into window.dataLayer for GTM → GA4.
 * All events in backend/docs/analytics/event-taxonomy.md route through this file.
 *
 * Rules (enforced by tests):
 *  - event names must be snake_case, no spaces
 *  - no PII in params (email, phone, name, full tokens) — callers are responsible
 *  - all pushes are best-effort; never throw to callers
 *  - dev builds console.log for debugging; prod stays silent
 */

import { providerLandingAnalyticsParams } from './providerLandingExperiment';
import { isExternalTrackingBlockedPath } from './productionTracking';

const isDev = process.env.NODE_ENV !== 'production';

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return null;
  if (!window.dataLayer) window.dataLayer = [];
  return window.dataLayer;
};

const hashString = (input) => {
  if (!input) return null;
  let h = 0;
  const s = String(input);
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return 'h_' + Math.abs(h).toString(36);
};

export const hashToken = (token, len = 8) => {
  if (!token) return null;
  const s = String(token);
  return s.length <= len ? s : s.substring(0, len);
};

const EVENT_NAME_RE = /^[a-z][a-z0-9_]*$/;

const assertValidEventName = (event) => {
  if (!EVENT_NAME_RE.test(event)) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error(
        `[dataLayer] Invalid event name: "${event}". Must be snake_case, lowercase, start with letter.`
      );
    }
    return false;
  }
  return true;
};

export const track = (event, params = {}) => {
  try {
    if (isExternalTrackingBlockedPath()) return;
    if (!assertValidEventName(event)) return;
    const dl = ensureDataLayer();
    if (!dl) return;
    const audience = params.audience || params.audience_selected;
    const path = window.location?.pathname || '';
    const isProviderEvent = audience === 'provider' || (!audience && (
      event.startsWith('provider_') || path === '/providers' ||
      path === '/register/provider' || path.startsWith('/provider/')
    ));
    const payload = { event, ...(isProviderEvent ? providerLandingAnalyticsParams() : {}), ...params };
    dl.push(payload);
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[dataLayer]', event, params);
    }
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error('[dataLayer] track failed:', e);
    }
  }
};

/**
 * Fire an event only once per lifetime for a given key.
 * Uses localStorage so it persists across sessions / devices (same browser).
 * Use for events like provider_first_swipe (first time per browser).
 */
export const trackOnce = (key, event, params = {}) => {
  try {
    if (!key) return;
    const storageKey = `rxr_fire_once:${key}`;
    if (typeof localStorage !== 'undefined' && localStorage.getItem(storageKey)) return;
    track(event, params);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(storageKey, String(Date.now()));
    }
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error('[dataLayer] trackOnce failed:', e);
    }
  }
};

/**
 * Session-scoped fire-once (resets on tab close).
 * Use for per-session events like registration_started.
 */
export const trackOncePerSession = (key, event, params = {}) => {
  try {
    if (!key) return;
    const storageKey = `rxr_fire_session:${key}`;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey)) return;
    track(event, params);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(storageKey, String(Date.now()));
    }
  } catch (e) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.error('[dataLayer] trackOncePerSession failed:', e);
    }
  }
};

export const identify = (user) => {
  if (!user) return;
  track('identify', {
    user_id: user.id || user.sub || null,
    user_type: user.type || user.userType || user.role || null,
    provider_type: user.providerType || null,
    group_type: user.groupType || null,
    subscription_tier: user.subscriptionTier || 'free',
  });
};

export const resetIdentity = () => {
  track('identify_reset', {
    user_id: null,
    user_type: 'visitor',
    provider_type: null,
    group_type: null,
    subscription_tier: null,
  });
};

/**
 * Send via navigator.sendBeacon when available so the event survives unload.
 * GTM's standard dataLayer.push doesn't guarantee in-flight delivery; for
 * abandonment events we ALSO push to a backend beacon endpoint.
 */
export const trackBeacon = (event, params = {}) => {
  if (isExternalTrackingBlockedPath()) return;
  track(event, params);
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const payload = JSON.stringify({ event, params, ts: Date.now() });
      const blob = new Blob([payload], { type: 'application/json' });
      const endpoint = process.env.REACT_APP_ANALYTICS_BEACON_URL;
      if (endpoint) navigator.sendBeacon(endpoint, blob);
    }
  } catch (e) {
    // silent
  }
};

// Internal helper used by tests to reset state
export const __resetForTests = () => {
  if (typeof window !== 'undefined') window.dataLayer = [];
  try {
    if (typeof localStorage !== 'undefined') {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('rxr_fire_once:'))
        .forEach((k) => localStorage.removeItem(k));
    }
    if (typeof sessionStorage !== 'undefined') {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('rxr_fire_session:'))
        .forEach((k) => sessionStorage.removeItem(k));
    }
  } catch (_) {
    // ignore
  }
};

export { hashString };
